import { useCallback, useEffect, useState } from "react"
import { DOMAIN_API_HOST } from "../../../../../configs/main"
import { useSnackbar } from "notistack";
import { Switch, Dialog, DialogTitle, DialogContent, IconButton, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import { Banners, getAdEditLockState } from "./Banners";
import useUser from "../../../../../data/useUser";
import { trackWizardEvent } from "../../../../../utils/productEvents";

const REJECT_REASONS = [
  "Отсутствие на баннере ИНН и наименования юридического лица",
  "Загрузка разных креативов в рамках одного объявления",
  "Запрещенная в соответствии с законом “О рекламе” тематика",
  "Запрещенные, в соответствии с законом “О рекламе” слова и выражения",
  "Отсутствие дисклеймера (мед услуги)",
  "Несоответствие ИНН на баннере реквизитам юридического лица",
  "Указанная посадочная страница не открывается",
  "Недопустимые ресайзы баннеров",
];

function isFeedRejected(feed) {
  return feed?.yml_feed_moderate_status === "REJECTED";
}

function FeedRejectNotice({ feed, isMobile = false }) {
  if (!isFeedRejected(feed)) return null;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 10px",
        borderRadius: 6,
        background: "#fff7ed",
        border: "1px solid #fed7aa",
      }}
    >
      <span style={{ fontSize: isMobile ? 11 : 12, color: "#9a3412", lineHeight: 1.45, fontWeight: 600 }}>
        Прайс‑лист отклонён
      </span>
    </div>
  );
}

const PREVIEW_SLOT_COUNT = 3;

function getAdStatusMeta(ad) {
  const moderateStatus = ad.moderate_status;

  if (moderateStatus === "REJECTED") {
    return { label: "Отклонено", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" };
  }
  if (ad.status === "ACTIVE" && moderateStatus === "MODERATING") {
    return { label: "На модерации", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" };
  }
  if (ad.status === "ACTIVE" && moderateStatus === "ACCEPTED") {
    return { label: "Активно", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
  }
  return { label: "Выключено", color: "#52525b", bg: "#f4f4f5", border: "#e4e4e7" };
}

const AdListItem = ({ ad, isMobile, onEdit, onToggleStatus }) => {
  const status = getAdStatusMeta(ad);
  const previewBanners = [...(ad.banners || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  ).slice(0, PREVIEW_SLOT_COUNT);
  const previewSlots = Array.from(
    { length: PREVIEW_SLOT_COUNT },
    (_, index) => previewBanners[index] || null
  );
  const thumbSize = isMobile ? 56 : 64;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 12 : 16,
        padding: isMobile ? "14px" : "16px 20px",
        borderRadius: 10,
        background: "#fff",
        border: `1px solid ${status.border}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.15s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          flexShrink: 0,
          width: thumbSize * PREVIEW_SLOT_COUNT + 8 * (PREVIEW_SLOT_COUNT - 1),
        }}
      >
        {previewSlots.map((banner, index) => (
          <div
            key={banner?.id || `empty-${index}`}
            style={{
              width: thumbSize,
              height: thumbSize,
              borderRadius: 8,
              overflow: "hidden",
              background: banner ? "#f0f1f3" : "#fafafa",
              border: banner ? "none" : "1px dashed #e4e4e7",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {banner ? (
              banner.extension?.toLowerCase() === "mp4" ? (
                <video
                  src={banner.url}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  muted
                  referrerPolicy="unsafe-url"
                />
              ) : (
                <img
                  src={banner.url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  referrerPolicy="unsafe-url"
                />
              )
            ) : (
              <ImageOutlinedIcon
                sx={{
                  fontSize: isMobile ? 24 : 28,
                  color: "#d4d4d8",
                }}
              />
            )}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: isMobile ? 15 : 16, color: "#18181b", marginBottom: 4, wordBreak: "break-word" }}>
          {ad.head || "Новое объявление"}
        </div>
        {ad.text && (
          <div
            style={{
              fontSize: isMobile ? 12 : 13,
              color: "#71717a",
              marginBottom: 6,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {ad.text}
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: isMobile ? 11 : 12, color: "#a1a1aa" }}>
          <span>{ad.banners?.length || 0} баннер(ов)</span>
          <span>·</span>
          <span>{new Date(ad.created_at).toLocaleDateString("ru-RU")}</span>
        </div>
        {ad.moderate_status === "REJECTED" && (
          <div style={{ marginTop: 8, fontSize: isMobile ? 11 : 12, color: "#dc2626", lineHeight: 1.4 }}>
            {ad.reject_reason || "Модерация отклонена. Свяжитесь со службой поддержки."}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          alignItems: isMobile ? "center" : "flex-end",
          justifyContent: isMobile ? "space-between" : "flex-start",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            padding: "4px 10px",
            borderRadius: 20,
            fontSize: isMobile ? 11 : 12,
            fontWeight: 600,
            color: status.color,
            background: status.bg,
            whiteSpace: "nowrap",
          }}
        >
          {status.label}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <Switch
            size={isMobile ? "small" : "medium"}
            checked={ad.status === "ACTIVE"}
            onChange={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const confirm = window.confirm(
                `Вы действительно хотите ${e.target.checked ? "включить" : "выключить"} объявление?`
              );
              if (!confirm) return;
              await onToggleStatus(ad.id, e.target.checked);
            }}
          />
          <button
            type="button"
            onClick={() => onEdit(ad.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: isMobile ? "6px 10px" : "8px 14px",
              borderRadius: 6,
              border: "none",
              background: "#DFE8FF",
              color: "#3F73F9",
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
            Редактировать
          </button>
        </div>
      </div>
    </div>
  );
};

export const Ads = ({
  campaignId,
  openAd,
  currentAdId: _currentAdId,
  upd,
  setUpd,
  updBanner,
  setUpdBanner,
  setHasUnsavedChanges,
  hasUnsavedChanges = false,
  saveFormRef,
  isMobile = false,
  changeTab,
  disable = false,
  mediaPlanExpired = false,
  showNextButton = false,
}) => {
  const { user } = useUser();
  const [ads, setAds] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalAdId, setModalAdId] = useState(null)
  const [modalMode, setModalMode] = useState("edit")
  const [isCreating, setIsCreating] = useState(false)
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("token")
      )
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/fetch?campaign_id=${campaignId}`, {
        method: "GET",
        headers: myHeaders,
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const adsOnly = list.filter((item) => item && item.type !== "feed");
        const sortedAds = [...adsOnly].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setAds(sortedAds);
      }
    }
    fetchData()
  }, [upd, updBanner, campaignId])

  const createNewAd = async () => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    )
    const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/create?campaign_id=${campaignId}`, {
      method: "POST",
      body: JSON.stringify({}),
      headers: myHeaders,
    });
    if (response.ok) {
      const data = await response.json();
      trackWizardEvent("wizard_tab_ads", campaignId);
      return data
    }
  }

  const openModal = async (adId, mode = "edit") => {
    if (hasUnsavedChanges && modalOpen && modalAdId && modalAdId !== adId) {
      const confirmSave = window.confirm("Вы не сохранили объявление, сохранить?");
      if (confirmSave) {
        await saveFormRef?.current?.(modalAdId);
      }
    }
    setModalMode(mode);
    setModalAdId(adId);
    setModalOpen(true);
    openAd(adId);
  };

  const modalTitle =
    modalMode === "create"
      ? "Создание объявления"
      : (() => {
          const ad = ads.find((item) => item.id === modalAdId);
          return ad?.head ? `Редактирование объявления — ${ad.head}` : "Редактирование объявления";
        })();

  const closeModal = async (force = false) => {
    if (!force && hasUnsavedChanges) {
      const confirmSave = window.confirm("Вы не сохранили объявление, сохранить?");
      if (confirmSave) {
        await saveFormRef?.current?.(modalAdId);
      }
    }
    setModalOpen(false);
    setModalAdId(null);
    setHasUnsavedChanges?.(false);
    openAd(null, false, true);
  };

  const handleCreateAd = async () => {
    const confirm = window.confirm("Вы действительно хотите создать новое объявление?");
    if (!confirm) return;
    setIsCreating(true);
    try {
      const ad = await createNewAd();
      if (ad?.id) {
        setUpd((value) => !value);
        await openModal(ad.id, "create");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const updateStatus = async (id, status) => {
    if (status) {
      const enableLock = getAdEditLockState({
        mediaPlanExpired,
        status: "STOPPED",
        hasImpressions: false,
      });
      if (enableLock.locked) {
        enqueueSnackbar(enableLock.message, { variant: "info" });
        return;
      }
    }

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    )
    try {
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/update?_id=${id}`, {
        method: "POST",
        body: JSON.stringify({
          status: status ? 'ACTIVE' : 'STOPPED'
        }),
        headers: myHeaders,
      });
      if (response.ok) {
        setUpd((value) => !value);
        if (status) {
          trackWizardEvent("ads_enabled", campaignId);
        }
        enqueueSnackbar(`Объявление успешно ${status ? 'включено' : 'выключено'}`, { variant: "success" });
      } else {
        let errorMessage = "Ошибка при изменении статуса";
        try {
          const errorData = await response.json();
          if (errorData?.detail) errorMessage = String(errorData.detail);
        } catch (_) {}
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Ошибка сети при изменении статуса", { variant: "error" });
    }
  }

  const hasAds = ads.length > 0;
  const listSubtitle = hasAds
    ? `Всего объявлений: ${ads.length}`
    : "Создайте первое объявление для кампании";

  return (
    <div
      style={{
        width: "100%",
        padding: isMobile ? "16px" : "20px 24px 32px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, color: "#18181b" }}>Объявления</h2>
          <p style={{ margin: "6px 0 0", fontSize: isMobile ? 12 : 13, color: "#71717a" }}>
            {listSubtitle}
          </p>
        </div>
        <button
          type="button"
          disabled={isCreating}
          onClick={handleCreateAd}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: isMobile ? "10px 16px" : "10px 20px",
            borderRadius: 8,
            border: "none",
            background: isCreating ? "#a5b4fc" : "#3F73F9",
            color: "#fff",
            fontSize: isMobile ? 14 : 15,
            fontWeight: 600,
            cursor: isCreating ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <AddIcon sx={{ fontSize: 20 }} />
          {isCreating ? "Создание…" : "Создать объявление"}
        </button>
      </div>

      {!hasAds ? (
        <div
          style={{
            marginTop: 16,
            padding: isMobile ? "32px 20px" : "48px 32px",
            borderRadius: 12,
            border: "2px dashed #e4e4e7",
            background: "#fafafa",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, color: "#52525b", marginBottom: 8 }}>
            Объявлений пока нет
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: "#a1a1aa", marginBottom: 20 }}>
            Нажмите «Создать объявление», чтобы добавить баннеры и тексты
          </div>
          <button
            type="button"
            disabled={isCreating}
            onClick={handleCreateAd}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#3F73F9",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
            Создать объявление
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          {ads.map((ad) => (
            <AdListItem
              key={ad.id}
              ad={ad}
              isMobile={isMobile}
              onEdit={openModal}
              onToggleStatus={updateStatus}
            />
          ))}
        </div>
      )}

      {showNextButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <LoadingButton
            loadingPosition="end"
            variant="contained"
            disabled={disable}
            onClick={() => {
              trackWizardEvent("wizard_tab_ads", campaignId);
              if (!user?.is_trial || user.is_trial === false) {
                changeTab(null, "4");
              } else {
                changeTab(null, "5");
              }
            }}
            endIcon={<SendIcon />}
          >
            Далее
          </LoadingButton>
        </Box>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => closeModal(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
            pb: 1,
            pt: isMobile ? 2 : 2.5,
            pl: isMobile ? 2 : 3,
            fontSize: isMobile ? 17 : 20,
            fontWeight: 600,
            color: "#18181b",
          }}
        >
          {modalTitle}
          <IconButton
            onClick={() => closeModal(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Закрыть"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 1 }}>
          {modalAdId && (
            <Banners
              campaignId={campaignId}
              currentAdId={modalAdId}
              changeTab={changeTab}
              disable={disable}
              mediaPlanExpired={mediaPlanExpired}
              upd={upd}
              setUpd={setUpd}
              updBanner={updBanner}
              setUpdBanner={setUpdBanner}
              setHasUnsavedChanges={setHasUnsavedChanges}
              saveFormRef={saveFormRef}
              isMobile={isMobile}
              hideNextButton
              inModal
              onClose={closeModal}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}






function formatYmlParentLabel(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function YmlFeedPendingSection({ pending, loading, expanded, onToggleExpanded }) {
  if (loading) {
    return (
      <div style={{ fontSize: 12, color: "#52525b", padding: "8px 0" }}>
        Загрузка сведений об изменениях YML…
      </div>
    );
  }
  if (pending == null || (typeof pending === "object" && Object.keys(pending).length === 0)) {
    return null;
  }
  if (pending.kind === "new_feed") {
    return (
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.45,
          color: "#1e3a5f",
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: 8,
          padding: "10px 12px",
        }}
      >
        Загружен новый прайс‑лист (новый URL / первая выгрузка). Требуется модерация фида.
      </div>
    );
  }
  if (pending.kind === "delta" && Array.isArray(pending.items) && pending.items.length > 0) {
    const items = pending.items;
    const limit = 8;
    const shown = expanded ? items : items.slice(0, limit);
    const hasMore = items.length > limit;
    return (
      <div
        style={{
          border: "1px solid #e4e4e7",
          borderRadius: 8,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, padding: "8px 10px", background: "#f4f4f5", color: "#18181b" }}>
          Изменения по категориям YML ({items.length})
        </div>
        <div style={{ maxHeight: 240, overflowY: "auto", padding: "6px 10px 10px" }}>
          {shown.map((item, idx) => {
            const ch = item.change ?? item.type;
            const yid = item.yml_category_id ?? item.category_id ?? "—";
            const nm = item.name ?? "";
            if (ch === "offers_count_changed") {
              const was = item.previous_offers_count ?? item.previous_count ?? item.was ?? "—";
              const now = item.current_offers_count ?? item.new_count ?? item.offers_count ?? item.now ?? "—";
              return (
                <div
                  key={idx}
                  style={{
                    borderBottom: "1px solid #f4f4f5",
                    padding: "8px 0",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 600, color: "#18181b" }}>
                    {nm || "Категория"} <span style={{ fontWeight: 500, color: "#71717a" }}>(yml_category_id: {yid})</span>
                  </div>
                  <div style={{ color: "#3f3f46", marginTop: 4 }}>
                    Было офферов: {was}, стало: {now}
                  </div>
                </div>
              );
            }
            if (ch === "new_category") {
              const cnt = item.current_offers_count ?? item.offers_count ?? "—";
              return (
                <div
                  key={idx}
                  style={{ borderBottom: "1px solid #f4f4f5", padding: "8px 0", fontSize: 12, color: "#3f3f46" }}
                >
                  Новая категория: {nm || "—"} (yml_category_id: {yid}), офферов: {cnt}
                </div>
              );
            }
            if (ch === "name_changed") {
              const prev = item.previous_name ?? "";
              const cur = item.current_name ?? "";
              return (
                <div
                  key={idx}
                  style={{ borderBottom: "1px solid #f4f4f5", padding: "8px 0", fontSize: 12, color: "#3f3f46" }}
                >
                  Категория {yid}: переименование: «{prev}» → «{cur}»
                </div>
              );
            }
            if (ch === "parent_changed") {
              const pp = formatYmlParentLabel(item.previous_parent);
              const cp = formatYmlParentLabel(item.current_parent);
              return (
                <div
                  key={idx}
                  style={{ borderBottom: "1px solid #f4f4f5", padding: "8px 0", fontSize: 12, color: "#3f3f46" }}
                >
                  Категория {yid}: изменён родитель: {pp} → {cp}
                </div>
              );
            }
            return (
              <div key={idx} style={{ borderBottom: "1px solid #f4f4f5", padding: "8px 0", fontSize: 11, color: "#71717a" }}>
                {String(ch || "изменение")}: {JSON.stringify(item)}
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button
            type="button"
            onClick={onToggleExpanded}
            style={{
              width: "100%",
              border: "none",
              borderTop: "1px solid #e4e4e7",
              background: "#fafafa",
              fontSize: 12,
              padding: "8px",
              cursor: "pointer",
              color: "#3F73F9",
              fontWeight: 500,
            }}
          >
            {expanded ? "Свернуть" : `Показать все (${items.length})`}
          </button>
        )}
      </div>
    );
  }
  return null;
}

export const AdsForManagers = ({ campaignId, isErid, setUpd, onDone, refreshKey, moderationFocus }) => {
  const [ads, setAds] = useState([])
  const [feedData, setFeedData] = useState(null)
  const [feedPendingChanges, setFeedPendingChanges] = useState(null)
  const [feedPendingLoading, setFeedPendingLoading] = useState(false)
  const [deltaExpanded, setDeltaExpanded] = useState(false)

  const showFeedBlock = Boolean(feedData && moderationFocus !== "ads")
  const showAdsBlock = moderationFocus !== "feed"

  const { enqueueSnackbar } = useSnackbar();

  const [erid, setErid] = useState("");
  const [canApprove, setCanApprove] = useState(false);
  const [rejectReasons, setRejectReasons] = useState({});

  const loadFeedPendingChanges = useCallback(async () => {
    if (moderationFocus === "ads") {
      setFeedPendingChanges(null);
      setFeedPendingLoading(false);
      return;
    }
    setFeedPendingLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", "Bearer " + localStorage.getItem("admin_token"));
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/campaigns/${campaignId}/yml/feed-pending-changes`,
        { method: "GET", headers: myHeaders }
      );
      if (res.ok) {
        const raw = await res.json();
        const pc =
          raw?.yml_feed_pending_changes ??
          raw?.data?.yml_feed_pending_changes ??
          (raw && typeof raw === "object" && "kind" in raw ? raw : null);
        setFeedPendingChanges(pc ?? null);
      } else {
        setFeedPendingChanges(null);
      }
    } catch {
      setFeedPendingChanges(null);
    } finally {
      setFeedPendingLoading(false);
    }
  }, [campaignId, moderationFocus]);

  useEffect(() => {
    setDeltaExpanded(false);
    void loadFeedPendingChanges();
  }, [campaignId, refreshKey, moderationFocus, loadFeedPendingChanges]);

  useEffect(() => {
    const fetchData = async () => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("admin_token")
      )
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/fetch?campaign_id=${campaignId}`, {
        method: "GET",
        headers: myHeaders,
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const feed = list.find((item) => item && item.type === "feed") || null;
        const adsOnly = list.filter((item) => item && item.type !== "feed");
        setFeedData(feed);
        const sortedAds = [...adsOnly].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setAds(sortedAds);
      }
    }
    fetchData()
  }, [campaignId, refreshKey])

  const rejectCampaign = async (advert_id, useCampaignId, rejectReason) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("admin_token")
    );
    const q = useCampaignId
      ? `campaign_id=${campaignId}`
      : `advert_id=${advert_id}`;
    const reasonQuery = rejectReason ? `&reject_reason=${encodeURIComponent(rejectReason)}` : "";
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/reject?${q}${reasonQuery}`, {
      method: "POST",
      headers: myHeaders,
    });
    if (res.ok) {
      const hybridFull = Boolean(feedData && ads.length > 0);
      const feedAccepted = feedData?.yml_feed_moderate_status === "ACCEPTED";
      const hasModeratingAds = ads.some(
        (a) => a.status === "ACTIVE" && a.moderate_status === "MODERATING"
      );
      if (moderationFocus === "ads") {
        if (useCampaignId) return;
        enqueueSnackbar(
          hybridFull
            ? "Объявление отклонено. Прайс‑лист при необходимости обработайте во вкладке «Товарка»."
            : "Объявление отклонено",
          { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else if (moderationFocus === "feed") {
        if (!useCampaignId) return;
        enqueueSnackbar(
          hybridFull && hasModeratingAds
            ? "Прайс‑лист отклонён. Объявления при необходимости обработайте во вкладке «Баннеры»."
            : "Фид-кампания отклонена",
          { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else {
        if (useCampaignId) {
          enqueueSnackbar(
            hybridFull && hasModeratingAds
              ? "Прайс‑лист отклонён. При необходимости обработайте объявления."
              : "Фид-кампания отклонена",
            { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        } else {
          enqueueSnackbar(
            hybridFull
              ? "Объявление отклонено. РК типа «объявления + фид» может требовать решения по прайс‑листу."
              : "Объявление отклонено",
            { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        }
        if (setUpd) setUpd((v) => !v);
        if (onDone) {
          if (useCampaignId) {
            if (!hybridFull || !hasModeratingAds) onDone();
          } else if (!hybridFull || feedAccepted) onDone();
        }
      }
      if (useCampaignId) void loadFeedPendingChanges();
      return;
    }
    let errorMessage = "Не удалось отклонить объявление";
    try {
      const errorData = await res.json();
      if (errorData?.detail) errorMessage = String(errorData.detail);
    } catch (_) {
      errorMessage = "Не удалось отклонить объявление";
    }
    enqueueSnackbar(errorMessage, {
      variant: "error",
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  }

  const aproveCampaign = async (advert_id, useCampaignId) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("admin_token")
    );
    const q = useCampaignId
      ? `campaign_id=${campaignId}`
      : `advert_id=${advert_id}`;
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/approve?${q}`, {
      method: "POST",
      headers: myHeaders,
    });
    if (res.ok) {
      const hybridFull = Boolean(feedData && ads.length > 0);
      const feedAccepted = feedData?.yml_feed_moderate_status === "ACCEPTED";
      const hasModeratingAds = ads.some(
        (a) => a.status === "ACTIVE" && a.moderate_status === "MODERATING"
      );
      if (moderationFocus === "ads") {
        if (useCampaignId) return;
        enqueueSnackbar(
          hybridFull
            ? "Объявление одобрено. Прайс‑лист примите во вкладке «Товарка»."
            : "Объявление одобрено.",
          { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else if (moderationFocus === "feed") {
        if (!useCampaignId) return;
        enqueueSnackbar(
          hybridFull && hasModeratingAds
            ? "Прайс‑лист одобрен. Объявления примите во вкладке «Баннеры»."
            : "Фид-кампания одобрена",
          { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else {
        if (useCampaignId) {
          enqueueSnackbar(
            hybridFull && hasModeratingAds
              ? "Прайс‑лист одобрен. Кампания остаётся на модерации, пока не приняты все объявления."
              : "Фид-кампания одобрена",
            { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        } else {
          enqueueSnackbar(
            hybridFull
              ? "Объявление одобрено. Кампания остаётся на модерации, пока не будет принято решение по прайс‑листу (YML)."
              : "Объявление одобрено.",
            { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        }
        if (setUpd) setUpd((v) => !v);
        if (onDone) {
          if (useCampaignId) {
            if (!hybridFull || !hasModeratingAds) onDone();
          } else if (!hybridFull || feedAccepted) onDone();
        }
      }
      if (useCampaignId) void loadFeedPendingChanges();
    }
  }


  return (
    <div style={{ border: "2px solid #e5e5ea", borderRadius: 15, background: "white", width: 460, overflowY: "auto"  }}>

      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", overflowY: "auto" }}>
          {moderationFocus !== "ads" && (
            <div style={{ width: "100%" }}>
              <YmlFeedPendingSection
                pending={feedPendingChanges}
                loading={feedPendingLoading}
                expanded={deltaExpanded}
                onToggleExpanded={() => setDeltaExpanded((v) => !v)}
              />
            </div>
          )}
          {moderationFocus === "feed" && !feedData && (
            <span style={{ fontSize: 14, margin: 10, color: "#b91c1c" }}>Прайс‑лист (YML) в ответе не найден.</span>
          )}
          {showFeedBlock && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 10, borderRadius: 5, background: "#fafafa", marginBottom: 10, border: "2px solid #e5e5ea" }}>
              <p style={{ fontWeight: "bold", fontSize: 16, margin: 0 }}>YML-фид</p>
              {feedData.yml_original_feed_url && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#18181b" }}>Исходный фид</span>
                  <textarea
                    readOnly
                    value={feedData.yml_original_feed_url}
                    rows={3}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      resize: "vertical",
                      minHeight: 56,
                      fontSize: 12,
                      lineHeight: 1.45,
                      color: "#111827",
                      border: "1px solid #e4e4e7",
                      borderRadius: 6,
                      padding: "8px 10px",
                      background: "#fff",
                      fontFamily: "inherit",
                    }}
                  />
                  <a
                    href={feedData.yml_original_feed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, color: "#3F73F9", fontWeight: 500 }}
                  >
                    Открыть в браузере
                  </a>
                  <FeedRejectNotice feed={feedData} />
                </div>
              )}
              {feedData.yml_referral_feed_url && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#18181b" }}>Реферальный фид</span>
                  <textarea
                    readOnly
                    value={feedData.yml_referral_feed_url}
                    rows={4}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      resize: "vertical",
                      minHeight: 72,
                      fontSize: 12,
                      lineHeight: 1.45,
                      color: "#111827",
                      border: "1px solid #e4e4e7",
                      borderRadius: 6,
                      padding: "8px 10px",
                      background: "#fff",
                      fontFamily: "inherit",
                    }}
                  />
                  <a
                    href={feedData.yml_referral_feed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, color: "#3F73F9", fontWeight: 500 }}
                  >
                    Открыть в браузере
                  </a>
                  {!feedData.yml_original_feed_url && <FeedRejectNotice feed={feedData} />}
                </div>
              )}
              {isErid && (
                <div style={{ display: "flex", gap: 10, marginTop: 8, justifyContent: "flex-end" }}>
                  <button
                    onMouseEnter={(e) => { e.target.style.backgroundColor = "#f3f3f4"; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
                    style={{ fontSize: 14, padding: "8px 16px", borderRadius: 5, border: "1px solid #e4e4e7", transition: "background-color 0.3s ease"}}
                    onClick={() => rejectCampaign(null, true)}
                  >
                    Отклонить
                  </button>
                  <button
                    onMouseEnter={(e) => { e.target.style.backgroundColor = "#22cd8d"; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = "#1dad77"; }}
                    style={{ color: "#fff", fontSize: 14, backgroundColor: "#1dad77", padding: "8px 20px", borderRadius: 5, transition: "background-color 0.3s ease"}}
                    onClick={() => aproveCampaign(null, true)}
                  >
                    Одобрить
                  </button>
                </div>
              )}
            </div>
          )}
          {showAdsBlock && !feedData && ads.length === 0 && (
            <span style={{ fontSize: 14, margin: 10 }}>Нет созданных объявлений</span>
          )}
          {!moderationFocus && feedData && ads.length === 0 && (
            <span style={{ fontSize: 13, margin: "4px 10px 0", color: "#52525b" }}>
              Объявлений нет — модерация только по прайс‑листу.
            </span>
          )}
          {showAdsBlock &&
            [...ads]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((ad) => (
              <div 
                key={ad.id}
                style={{ display: "flex", position: "relative", flexDirection: "column", gap: 5, padding: 10, borderRadius: 5, background: "#fafafa", marginBottom: 10, border: ad.status === "ACTIVE" && ad.moderate_status === "MODERATING" ? "2px solid orange" : ad.status === "ACTIVE" && ad.moderate_status === "ACCEPTED" ? "2px solid blue" : ad.status === "ACTIVE" && ad.moderate_status === "REJECTED" ? "2px solid red" : "2px solid #e5e5ea" }}>
                <p style={{ width: "75%", fontWeight: "bold", fontSize: 16, wordBreak: "break-all" }}>{ad.head || "Новое объявление"}</p>
                <p style={{ width: "75%", fontSize: 14, wordBreak: "break-all"}}>{ad.text || ""}</p>
                {ad.banners.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", gap: 10, overflowX: "auto", overflowY: "hidden", width: "100%", height: 200 }}>
                    {[...ad.banners]
                      .sort(
                        (a, b) =>
                          new Date(a.created_at).getTime() -
                          new Date(b.created_at).getTime()
                      )
                      .map((el, index) => (
                        <div
                          key={index}
                          style={{
                            minWidth: 200,
                            height: "100%",
                            borderRadius: 5,
                            background: "#f0f1f3",
                            position: "relative",
                          }}
                        >
                          {el.extension && el.extension.toLowerCase() === "mp4" ? (
                            <video
                              style={{
                                width: "200px",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              src={el.url}
                              referrerPolicy="unsafe-url"
                              muted
                              controls
                            />
                          ) : (
                            <img
                              style={{
                                width: "200px",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              src={el.url}
                              referrerPolicy="unsafe-url"
                              alt="banner"
                            />
                          )}
                          <a
                            href={el.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              position: "absolute",
                              top: 5,
                              right: 5,
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              opacity: 0.7,
                              borderRadius: "50%",
                              textDecoration: "none",
                              fontSize: 14,
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-square-arrow-out-up-right"><path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"/><path d="m21 3-9 9"/><path d="M15 3h6v6"/></svg>
                          </a>
                        </div>
                      ))}
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 5 }}>
                  <span style={{ fontSize: 12 }}>Статус: {ad.status === "ACTIVE" && ad.moderate_status === "MODERATING" ? "На модерации" : ad.status === "ACTIVE" && ad.moderate_status === "ACCEPTED" ? "Активно" : ad.status === "ACTIVE" && ad.moderate_status === "REJECTED" ? "Отклонено" : "Выключено"}</span>
                  {ad.moderate_status === "REJECTED" && (
                    <span style={{ fontSize: 12 }}>Причина отклонения: {ad.reject_reason || "Не указана"}</span>
                  )}
                  <span style={{ fontSize: 12 }}>Создано: {new Date(ad.created_at).toLocaleString()}</span>
                  <span style={{ fontSize: 12, cursor: "pointer", marginTop: "5px" }} onClick={
                    () => {
                      if (!ad.erid) return;
                      navigator.clipboard.writeText(ad.erid);
                      enqueueSnackbar("ERID скопирован в буфер обмена", { variant: "success" });
                    }
                  }>ERID: {ad.erid || "Не указан"}</span>
                  {/* <span style={{ fontSize: 12, cursor: "pointer", marginTop: "10px" }} onClick={
                    () => {
                      navigator.clipboard.writeText(ad.id);
                      enqueueSnackbar("ID скопирован в буфер обмена", { variant: "success" });
                    }
                  }>ID: {ad.id}</span> */}
                  {ad.landing_page && <span style={{ fontSize: 12, marginTop: "5px" }}>Посадочная: {ad.landing_page}</span>}
                  {ad.landing_page && (
                  <div>
                  <div style={{ marginTop: "10px" }}>
                    <div style={{ fontSize: 12, marginBottom: "5px" }}>Ссылка для внешних систем:</div>
                    <div 
                      style={{ 
                        fontSize: 12,
                        padding: "8px",
                        border: "1px solid #e4e4e7",
                        borderRadius: "4px",
                        backgroundColor: "#f9f9f9",
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        maxWidth: "100%",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none"
                      }}
                      onClick={() => {
                        const firstBanner = ad.banners && ad.banners.length > 0 ? ad.banners[0] : null;
                        const bannerId = firstBanner ? firstBanner.id : '';
                        
                        let utmMarkers = ad.utm_markers || `utm_source=${ad.signup_domain === "lk.retargeo.ru" ? "Retargeo" : "Programmatic"}&utm_medium={PRG}&utm_campaign={CAMPAIGN_ID}&utm_content={BANNER_ID}&utm_term={SITE_ID}`;
                        utmMarkers = utmMarkers
                          .replace(/\{CAMPAIGN_ID\}/g, campaignId)
                          .replace(/\{BANNER_ID\}/g, bannerId)
                          .replace(/&utm_term=\{SITE_ID\}/g, '');
                        
                        const separator = ad.landing_page && ad.landing_page.includes('?') ? '&' : '?';
                        const fullLink = `${ad.landing_page}${separator}${utmMarkers}`;
                        
                        navigator.clipboard.writeText(fullLink);
                        enqueueSnackbar("Ссылка скопирована в буфер обмена", { 
                          variant: "success",
                          autoHideDuration: 3000,
                          anchorOrigin: { vertical: "top", horizontal: "right" }
                        });
                      }}
                      title="Кликните для копирования ссылки"
                    >
                      {`${ad.landing_page}${ad.landing_page && ad.landing_page.includes('?') ? '&' : '?'}`}
                      {(ad.utm_markers || `utm_source=${ad.signup_domain === "lk.retargeo.ru" ? "Retargeo" : "Programmatic"}&utm_medium={PRG}&utm_campaign={CAMPAIGN_ID}&utm_content={BANNER_ID}&utm_term={SITE_ID}`)
                        .replace(/\{CAMPAIGN_ID\}/g, campaignId)
                        .replace(/\{BANNER_ID\}/g, ad.banners && ad.banners.length > 0 ? ad.banners[0].id : '')
                        .replace(/&utm_term=\{SITE_ID\}/g, '')}
                    </div>
                  </div>
                  <p style={{ fontSize: 12, marginTop: 5, fontStyle: "italic" }}>Не забудьте заменить текст {`{PRG}`}</p>
                </div>
                  )}
                </div>

                { isErid && (
                  <div>
                    <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "flex-end", marginBottom: 20 }}>
                      <div style={{ width: "100%"}}>
                        <p style={{ fontSize: 12, fontWeight: 500, marginBottom: 5 }}>Укажите erid из ОРД</p>
                        <input placeholder="Идентификатор креатива" style={{ width: "100%", border: "1px solid #e4e4e7", borderRadius: 5 }} type="text" onChange={(e) => setErid(e.target.value)} defaultValue={ad.erid} />
                      </div>
                      <button
                        onMouseEnter={(e) => { e.target.style.backgroundColor = "#f3f3f4"; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
                        style={{ fontSize: 14, padding: "8px 16px", borderRadius: 5, border: "1px solid #e4e4e7", transition: "background-color 0.3s ease", height: 33}}
                        onClick={() => {
                          const fetchApi = async () => {
                            const myHeaders = new Headers();
                            myHeaders.append("Content-Type", "application/json");
                            myHeaders.append(
                              "Authorization",
                              "Bearer " + localStorage.getItem("admin_token")
                            );
                            const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/erid?advert_id=${ad.id}&erid=${erid}`, {
                              method: "POST",
                              headers: myHeaders
                            });
                            if (res.ok) {
                              setCanApprove(true)
                              enqueueSnackbar("Креатив с erid - " + erid + " успешно найден", {
                                variant: "success",
                                autoHideDuration: 3000,
                                anchorOrigin: { vertical: "top", horizontal: "right" },
                              });
                            } else {
                              setCanApprove(false)
                              enqueueSnackbar("Произошла проблема, проверьте корректность erid или инн клиента.", {
                                variant: "error",
                                autoHideDuration: 3000,
                                anchorOrigin: { vertical: "top", horizontal: "right" },
                              });
                            }
                          };
                          fetchApi();
                        }}>
                        Проверить
                      </button>
                    </div>
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                      <p style={{ fontSize: 12, fontWeight: 500, marginBottom: 5 }}>Причина отклонения</p>
                      <select
                        value={rejectReasons[ad.id] || ""}
                        style={{ width: "100%", border: "1px solid #e4e4e7", borderRadius: 5, height: 33, background: "#fff" }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRejectReasons((prev) => ({
                            ...prev,
                            [ad.id]: value,
                          }));
                        }}
                      >
                        <option value="" disabled>Выберите причину</option>
                        {REJECT_REASONS.map((reason) => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                      <button
                        onMouseEnter={(e) => { e.target.style.backgroundColor = "#f3f3f4"; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
                        style={{ fontSize: 14, padding: "8px 16px", borderRadius: 5, border: "1px solid #e4e4e7", transition: "background-color 0.3s ease"}}
                        onClick={() => {
                          const selectedReason = rejectReasons[ad.id];
                          if (!selectedReason) {
                            enqueueSnackbar("Выберите причину отклонения", {
                              variant: "info",
                              autoHideDuration: 3000,
                              anchorOrigin: { vertical: "top", horizontal: "right" },
                            });
                            return;
                          }
                          rejectCampaign(ad.id, false, selectedReason)
                        }}>
                        Отклонить
                      </button>
                      <button
                        onMouseEnter={(e) => { e.target.style.backgroundColor = "#22cd8d"; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = "#1dad77"; }}
                        style={{ color: "#fff", fontSize: 14, backgroundColor: "#1dad77", padding: "8px 20px", borderRadius: 5, transition: "background-color 0.3s ease"}}
                        onClick={() => {
                          if (!canApprove) {
                            enqueueSnackbar("Обязательно укажите erid и проверьте его", {
                              variant: "info",
                              autoHideDuration: 3000,
                              anchorOrigin: { vertical: "top", horizontal: "right" },
                            });
                            return;
                          }
                          aproveCampaign(ad.id)
                        }}>
                        Одобрить
                      </button>
                    </div>
                  </div>
                )}
              </div>
          ))}
        </div>
      </div>
    </div>
  )
}